import React, { useMemo } from 'react';
import { motion, useTransform, useSpring } from 'framer-motion';
import { TimerMode } from '../TimerEffect';

interface RippleEffectProps {
  progress: number;
  size: number;
  color: string;
  secondaryColor?: string;
  opacity?: number;
  speed?: number;
  mode?: TimerMode;
}

const RippleEffect: React.FC<RippleEffectProps> = ({
  progress,
  size,
  color,
  secondaryColor,
  opacity = 0.5,
  speed = 1,
  mode = 'countdown',
}) => {
  // 根据模式和进度计算波纹频率
  const rippleFrequency = useMemo(() => {
    if (mode === 'countdown') {
      // 倒计时模式：随着进度增加波纹频率
      return Math.max(0.5, 2 - progress * 1.5) * speed;
    } else {
      // 计时模式：随着进度增加波纹频率
      return Math.min(2, 0.5 + progress * 1.5) * speed;
    }
  }, [progress, mode, speed]);

  // 使用spring为动画添加物理特性
  const springProgress = useSpring(progress, {
    damping: 20,
    stiffness: 80,
  });

  // 创建3个波纹圆环
  const rippleCircles = useMemo(() => {
    return [0, 1, 2].map((index) => {
      // 计算波纹大小
      const baseSize = size * 0.3; // 基础大小
      const maxSize = size * (0.5 + index * 0.15); // 最大大小

      return (
        <motion.circle
          key={index}
          cx={size / 2}
          cy={size / 2}
          r={baseSize}
          fill="none"
          stroke={index % 2 === 0 ? color : secondaryColor || color}
          strokeWidth={Math.max(1, size / 40)}
          strokeOpacity={opacity / (index + 1)}
          animate={{
            r: [baseSize, maxSize, baseSize],
            scale: [1, 1.2, 1],
            opacity: [opacity, opacity / 2, opacity],
          }}
          transition={{
            duration: 2 / rippleFrequency,
            repeat: Infinity,
            repeatType: "loop",
            delay: index * (0.6 / rippleFrequency),
            ease: "easeInOut",
          }}
        />
      );
    });
  }, [size, color, secondaryColor, opacity, rippleFrequency]);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'none',
      }}
    >
      {rippleCircles}
    </svg>
  );
};

export default RippleEffect; 